import type { RankedSpace } from "@/features/vision-360/ai-mode/types";
import type { ResultItem } from "@/services/private-office-service/types";
import { getVipOfficePricePerPosition } from "@/features/vision-360/constants/vip-office-price-table";

type NewScoreDetails = {
  criteria?: Array<{
    label: string;
    score: number;
    in_tolerance: boolean;
    detail?: string;
  }>;
  in_tolerance_zone?: boolean;
  excluded_reason?: string;
};

type DetailedScoreItem = {
  criterion: string;
  requested: string;
  offered: string;
  matched: boolean;
};

export function isFiltersResultOutdated(item: ResultItem): boolean {
  if (item.is_outdated != null) {
    return item.is_outdated;
  }
  const days = parseInt(item.days_since_update ?? "", 10);
  if (
    isNaN(days) ||
    days < 0 ||
    item.threshold_days == null ||
    item.threshold_days <= 0
  ) {
    return false;
  }
  return days > item.threshold_days;
}

export function mapSearchResultToRankedSpaces(
  results: ResultItem[],
): RankedSpace[] {
  // Skip items without an id: they'd all collapse to the "" key and overwrite
  // each other, handing the wrong score_details to every id-less room.
  const resultById = new Map(
    results
      .filter((item) => item.private_room_id != null)
      .map((item) => [
        String(item.private_room_id),
        item as ResultItem & { score_details?: NewScoreDetails },
      ]),
  );
  return mapFiltersResultToRankedSpaces(results).map((space) => {
    const item = resultById.get(space.id);
    const newDetails = item?.score_details as NewScoreDetails | undefined;
    return {
      ...space,
      inToleranceZone: newDetails?.in_tolerance_zone ?? false,
      scoreBreakdown: newDetails?.criteria?.map((c) => ({
        label: c.label,
        score: c.score,
        inTolerance: c.in_tolerance,
        detail: c.detail,
      })),
    };
  });
}

export function mapFiltersResultToRankedSpaces(
  results: ResultItem[],
): RankedSpace[] {
  if (!Array.isArray(results)) return [];

  return results.map((item) => {
    const details = item.details;
    const spaceDetails = item.space_details;
    const itemRecord = item as Record<string, unknown>;
    const sourceFromApi =
      typeof itemRecord.source === "string" ? itemRecord.source : null;
    const capacity = Number(item.capacity ?? 0);
    const itemAny = item as Record<string, unknown>;
    const estimatedMonthlyPrice =
      typeof itemAny.estimated_monthly_price === "number"
        ? itemAny.estimated_monthly_price
        : null;
    let monthlyPrice =
      estimatedMonthlyPrice ??
      (item.monthly_price != null ? Number(item.monthly_price) : null);
    // VIP offices (integration source) can arrive without a price — reconstruct
    // it from the VIP price table so results and combo totals don't show R$ 0.
    const originFromApi =
      typeof itemRecord.origin === "string" ? itemRecord.origin : null;
    if (
      originFromApi === "integration" &&
      sourceFromApi === "vip-office" &&
      (!monthlyPrice || monthlyPrice === 0) &&
      capacity > 0
    ) {
      const pricePerPos = getVipOfficePricePerPosition(
        String(item.label ?? ""),
        String(spaceDetails?.name ?? item.label ?? ""),
        String(spaceDetails?.neighborhood ?? ""),
        String(spaceDetails?.cityname ?? ""),
      );
      if (pricePerPos > 0) monthlyPrice = pricePerPos * capacity;
    }
    const photos: string[] = [];
    if (details) {
      const d = details as Record<string, unknown>;
      const picKeys = [
        "picture_url",
        "picture_url1",
        "picture_url2",
        "picture_url3",
        "picture_url4",
        "picture_url5",
      ];
      picKeys.forEach((key) => {
        const p = d[key];
        if (typeof p === "string" && p.trim()) photos.push(p);
      });
    }
    const amenitiesStr = item.details?.amenities ?? item.amenities ?? "";
    const amenities =
      typeof amenitiesStr === "string" && amenitiesStr
        ? amenitiesStr
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

    const spaceAmenitiesStr = spaceDetails?.amenities ?? "";
    const spaceAmenities =
      typeof spaceAmenitiesStr === "string" && spaceAmenitiesStr
        ? spaceAmenitiesStr
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

    let score = 0;
    if (
      item.match_percent != null &&
      String(item.match_percent).trim() !== ""
    ) {
      const parsed = parseFloat(String(item.match_percent));
      if (!Number.isNaN(parsed)) score = Math.min(100, Math.max(0, parsed));
    }

    const addressParts = [
      spaceDetails?.address,
      spaceDetails?.neighborhood,
      spaceDetails?.cityname,
    ].filter(Boolean);
    const address = addressParts.length > 0 ? addressParts.join(", ") : "";

    const detailedScoreAnalysis = Array.isArray(item.detailed_score_analysis)
      ? (item.detailed_score_analysis as DetailedScoreItem[])
      : undefined;
    const scoreDetails = item.score_details ?? undefined;

    return {
      id: String(item.private_room_id ?? ""),
      searchId: item.search_id != null ? Number(item.search_id) : null,
      partnerId: item.space_id != null ? Number(item.space_id) : undefined,
      spaceName: String(spaceDetails?.name ?? ""),
      roomName: String(item.label ?? ""),
      city: String(spaceDetails?.cityname ?? ""),
      neighborhood: spaceDetails?.neighborhood ?? undefined,
      address,
      lat: Number(spaceDetails?.latitude ?? 0) || 0,
      lng: Number(spaceDetails?.longitude ?? 0) || 0,
      pricePerWorkstation:
        monthlyPrice != null && capacity > 0
          ? Math.round(monthlyPrice / capacity)
          : 0,
      monthlyPrice,
      credits:
        item.monthly_credit_price != null
          ? Math.ceil(Number(item.monthly_credit_price))
          : null,
      capacity,
      sizeM2: item.size != null ? Number(item.size) : null,
      score,
      amenities,
      spaceAmenities,
      // Booleans only — never the file URLs: this object is copied as-is into
      // the client-facing proposal (`selected_spaces`). PED-14095.
      spaceHasAvcb: spaceDetails?.has_avcb ?? undefined,
      spaceHasOperatingPermit: spaceDetails?.has_permit ?? undefined,
      imageUrl: photos[0] ?? "",
      images: photos,
      origin: typeof item.origin === "string" ? item.origin : null,
      source: sourceFromApi,
      salesPitch: undefined,
      matchReasons: undefined,
      detailedScoreAnalysis,
      scoreDetails: scoreDetails ?? null,
      isOutdated: isFiltersResultOutdated(item) || undefined,
      daysSinceUpdate: (() => {
        const n = parseInt(item.days_since_update ?? "", 10);
        return !isNaN(n) && n >= 0 ? n : undefined;
      })(),
      availabilityDate:
        typeof item.availabilityDate === "string"
          ? item.availabilityDate
          : null,
      spacePhotos: Array.isArray(spaceDetails?.photos)
        ? spaceDetails.photos.filter(Boolean)
        : [],
    };
  });
}
